import React, { useEffect, useState } from "react";
import { Cpu, Search, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import { ItemsPerPageDropdown } from "../../../components/ItemsPerPageDropdown ";

export interface HardwareItem {
    id: number;
    created?: string;
    updated?: string;
    is_deleted?: boolean;
    organization_id?: number;
    client_id?: number;
    agent_id?: number;
    asset_id?: number;
    component_type: string;
    name: string;
    description: string;
    serial_number: string | null;
    model_name: string | null;
    status: string | null;
}

interface HardwareInfoTabProps {
    client_id?: number | string;
    asset_id?: number | string;
    hideHeader?: boolean;
    defaultItemsPerPage?: number;
}

const HardwareInfoTab: React.FC<HardwareInfoTabProps> = ({
    client_id: propClientId,
    asset_id: propAssetId,
    hideHeader = false,
    defaultItemsPerPage = 10,
}) => {
    const [data, setData] = useState<HardwareItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    // Pagination state
    const [totalItems, setTotalItems] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

    const info = JSON.parse(sessionStorage.getItem("internal_ids") || "{}");
    const asset_id = propAssetId !== undefined && propAssetId !== null && propAssetId !== "" ? propAssetId : info.asset_id;
    const client_id = propClientId !== undefined && propClientId !== null && propClientId !== "" ? propClientId : info.client_id;

    const fetchData = async () => {
        setLoading(true);
        try {
            const skip = (currentPage - 1) * itemsPerPage;
            const limit = itemsPerPage;

            const queryObj: any = {};
            if (asset_id !== undefined && asset_id !== null && asset_id !== "") {
                queryObj.asset_id = typeof asset_id === "string" ? parseInt(asset_id, 10) : asset_id;
            }
            if (client_id !== undefined && client_id !== null && client_id !== "") {
                queryObj.client_id = typeof client_id === "string" ? parseInt(client_id, 10) : client_id;
            }

            if (debouncedSearchTerm) {
                queryObj["$or"] = [
                    { name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { component_type: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { model_name: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { serial_number: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { description: { "$ilike": `%${debouncedSearchTerm}%` } },
                    { status: { "$ilike": `%${debouncedSearchTerm}%` } }
                ];
            }

            const params: any = {
                query: JSON.stringify(queryObj),
                skip,
                limit,
            };

            if (sortConfig) {
                params.sort_by = sortConfig.key;
                params.sort_order = sortConfig.direction;
            }

            const response = await baseapi.get("/hardware_info", { params });
            if (response.data.status) {
                setData(response.data.data || []);
                setTotalItems(response.data.total || 0);
            } else {
                showError(response.data.message || "Failed to fetch hardware info");
            }
        } catch (error: any) {
            showError(error?.response?.data?.message || error?.message || "Failed to fetch hardware info");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchTerm(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    useEffect(() => {
        fetchData();
    }, [currentPage, itemsPerPage, sortConfig, debouncedSearchTerm]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(totalItems / itemsPerPage);

    const isStatusOk = (status: string | null) => {
        if (!status) return false;
        const s = status.toLowerCase();
        return s === 'ok' || s === 'up' || s === 'on' || s === 'active';
    };

    return (
        <div className="space-y-4 animate-fade-in">
            {!hideHeader ? (
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
                    <div className="flex items-center">
                        <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                            <Cpu className="w-4 h-4 text-white" />
                        </div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">Hardware Information</h2>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-200 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search hardware..."
                                className="w-full bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-gray-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-orange-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => fetchData()}
                            className="p-2 bg-slate-50 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded-lg text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
                        >
                            <RotateCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            ) : (
                <div className="flex justify-between items-center gap-3 mb-3">
                    <div className="flex items-center gap-2">
                        <div className="p-1 bg-orange-600/20 text-orange-400 rounded">
                            <Cpu className="w-3.5 h-3.5" />
                        </div>
                        <span className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">Hardware Info</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <div className="relative w-48 sm:w-64">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-gray-200 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search hardware..."
                                className="w-full bg-slate-50 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 rounded-lg pl-8 pr-3 py-1.5 text-xs text-gray-900 dark:text-white placeholder-slate-400 focus:outline-none focus:border-orange-500"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <button
                            onClick={() => fetchData()}
                            className="p-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-slate-500 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white transition-colors"
                            title="Refresh"
                        >
                            <RotateCcw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            )}

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 overflow-hidden shadow-sm dark:shadow-2xl">
                <div className="overflow-x-auto custom-scroll">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 dark:bg-gray-900/50 border-b border-slate-200 dark:border-gray-700">
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        
                                        Component Type
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap cursor-pointer" onClick={() => handleSort('name')}>
                                    <div className="flex items-center gap-1.5">
                                        
                                        Name
                                        {sortConfig?.key === 'name' && (sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                       
                                        Description
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                     
                                        Serial Number
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        
                                        Model Name
                                    </div>
                                </th>
                                <th className="px-4 py-3 text-xs font-semibold text-slate-500 dark:text-gray-200 uppercase tracking-wider whitespace-nowrap">
                                    <div className="flex items-center gap-1.5">
                                        
                                        Status
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-gray-700">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center">
                                        <div className="flex flex-col items-center gap-2">
                                            <RotateCcw className="w-8 h-8 text-orange-500 animate-spin" />
                                            <span className="text-slate-500 dark:text-gray-200">Loading hardware info...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : data.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="py-20 text-center text-slate-500 dark:text-gray-200">
                                        No hardware information found
                                    </td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-gray-700/30 transition-colors">
                                        <td className="px-4 py-3 text-sm font-medium text-white whitespace-nowrap">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20">
                                                {item.component_type
                                                    .replace(/_/g, " ")
                                                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-200 whitespace-nowrap">{item.name || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-gray-200 whitespace-nowrap">{item.description || '-'}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-gray-200 font-mono whitespace-nowrap">{item.serial_number && item.serial_number !== '""' ? item.serial_number : '-'}</td>
                                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-gray-200 whitespace-nowrap">{item.model_name && item.model_name !== '""' ? item.model_name : '-'}</td>
                                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                                            {item.status ? (
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase ${isStatusOk(item.status)
                                                    ? 'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20'
                                                    : 'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20'
                                                    }`}>
                                                    {item.status}
                                                </span>
                                            ) : '-'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Footer */}
                <div className="bg-slate-50 dark:bg-gray-900/40 px-4 py-2 border-t border-slate-200 dark:border-gray-700">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-slate-500 dark:text-gray-200">Show</span>
                                <ItemsPerPageDropdown
                                    value={itemsPerPage}
                                    onChange={(val) => {
                                        setItemsPerPage(val);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                            <span className="text-xs text-slate-500 dark:text-gray-200">
                                Showing {totalItems > 0 ? Math.min((currentPage - 1) * itemsPerPage + 1, totalItems) : 0} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} items
                            </span>
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                                className="p-2 text-slate-400 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &laquo;
                            </button>
                            <button
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="p-2 text-slate-400 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &lsaquo;
                            </button>
                            <div className="bg-orange-600 text-white px-3 py-1 rounded text-sm font-bold">
                                {currentPage}
                            </div>
                            <button
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 text-slate-400 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &rsaquo;
                            </button>
                            <button
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage === totalPages || totalPages === 0}
                                className="p-2 text-slate-400 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white disabled:opacity-30 transition-colors"
                            >
                                &raquo;
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HardwareInfoTab;
